import * as React from 'react';
import { Spinner } from '@components/base/feedback';
import { Button } from '@components/base/actions';
import { Text } from '@components/base/display';
import { VStack, HStack, Card, CardContent } from '@components/base/layout';
import { ShowcasePreviewSection } from '../../components/shared/ShowcasePreviewSection';
import { ShowcaseDemoSection } from '../../components/shared/ShowcaseDemoSection';
import type { ShowcaseDefinition, ShowcaseProps } from '../types';

type SpinnerSize = 'xs' | 'sm' | 'md' | 'lg';

function SpinnerShowcaseComponent({ playgroundProps, isDarkMode: _isDarkMode }: Readonly<ShowcaseProps>) {
  // State for button loading demo
  const [loadingButtons, setLoadingButtons] = React.useState<Record<string, boolean>>({});

  const simulateLoading = (buttonId: string) => {
    setLoadingButtons((prev) => ({ ...prev, [buttonId]: true }));
    setTimeout(() => {
      setLoadingButtons((prev) => ({ ...prev, [buttonId]: false }));
    }, 2000);
  };

  return (
    <VStack gap={6}>
      {/* Interactive Preview */}
      <ShowcasePreviewSection componentName="spinner">
          <div className="flex items-center justify-center p-8">
            <Spinner
              size={playgroundProps.size as SpinnerSize}
              color={playgroundProps.color as string}
            />
          </div>
      </ShowcasePreviewSection>

      {/* Sizes */}
      <ShowcaseDemoSection
        title="Sizes"
        description="Four preset sizes from extra small to large"
       
      >
          <HStack gap={8} align="center">
            <VStack gap={2} align="center">
              <Spinner size="xs" />
              <Text size="xs" color="secondary">xs (16px)</Text>
            </VStack>
            <VStack gap={2} align="center">
              <Spinner size="sm" />
              <Text size="xs" color="secondary">sm (24px)</Text>
            </VStack>
            <VStack gap={2} align="center">
              <Spinner size="md" />
              <Text size="xs" color="secondary">md (32px)</Text>
            </VStack>
            <VStack gap={2} align="center">
              <Spinner size="lg" />
              <Text size="xs" color="secondary">lg (48px)</Text>
            </VStack>
          </HStack>
      </ShowcaseDemoSection>

      {/* Custom Colors */}
      <ShowcaseDemoSection
        title="Custom Colors"
        description="Use any valid CSS color value"
       
      >
          <HStack gap={8} align="center">
            <VStack gap={2} align="center">
              <Spinner color="#047857" />
              <Text size="xs" color="secondary">Success</Text>
            </VStack>
            <VStack gap={2} align="center">
              <Spinner color="#B45309" />
              <Text size="xs" color="secondary">Warning</Text>
            </VStack>
            <VStack gap={2} align="center">
              <Spinner color="#B91C1C" />
              <Text size="xs" color="secondary">Error</Text>
            </VStack>
            <VStack gap={2} align="center">
              <Spinner color="#0369A1" />
              <Text size="xs" color="secondary">Info</Text>
            </VStack>
            <VStack gap={2} align="center">
              <Spinner color="#7C3AED" />
              <Text size="xs" color="secondary">Custom</Text>
            </VStack>
          </HStack>
      </ShowcaseDemoSection>

      {/* Custom Numeric Size */}
      <ShowcaseDemoSection
        title="Custom Numeric Size"
        description="Pass a number to specify exact pixel size"
       
      >
          <HStack gap={8} align="end">
            <VStack gap={2} align="center">
              <Spinner size={20} />
              <Text size="xs" color="secondary">20px</Text>
            </VStack>
            <VStack gap={2} align="center">
              <Spinner size={36} />
              <Text size="xs" color="secondary">36px</Text>
            </VStack>
            <VStack gap={2} align="center">
              <Spinner size={56} />
              <Text size="xs" color="secondary">56px</Text>
            </VStack>
            <VStack gap={2} align="center">
              <Spinner size={80} />
              <Text size="xs" color="secondary">80px</Text>
            </VStack>
          </HStack>
      </ShowcaseDemoSection>

      {/* With Loading Text */}
      <ShowcaseDemoSection
        title="With Loading Text"
        description="Combine spinner with descriptive text"
       
      >
          <VStack gap={6}>
            <HStack gap={3} align="center">
              <Spinner size="sm" />
              <Text>Loading claims data...</Text>
            </HStack>
            <HStack gap={3} align="center">
              <Spinner size="sm" color="#047857" />
              <Text>Processing payment...</Text>
            </HStack>
            <VStack gap={2} align="center" className="py-4">
              <Spinner size="lg" />
              <Text color="secondary">Please wait while we fetch your policies</Text>
            </VStack>
          </VStack>
      </ShowcaseDemoSection>

      {/* In Buttons */}
      <ShowcaseDemoSection
        title="In Buttons"
        description="Show loading state within buttons"
       
      >
          <HStack gap={4} wrap="wrap">
            <Button
              onClick={() => simulateLoading('save')}
              disabled={loadingButtons['save']}
            >
              {loadingButtons['save'] ? (
                <>
                  <Spinner size="xs" color="white" className="mr-2" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>

            <Button
              variant="secondary"
              onClick={() => simulateLoading('load')}
              disabled={loadingButtons['load']}
            >
              {loadingButtons['load'] ? (
                <>
                  <Spinner size="xs" className="mr-2" />
                  Loading...
                </>
              ) : (
                'Load More'
              )}
            </Button>

            <Button
              variant="danger"
              onClick={() => simulateLoading('delete')}
              disabled={loadingButtons['delete']}
            >
              {loadingButtons['delete'] ? (
                <>
                  <Spinner size="xs" color="white" className="mr-2" />
                  Deleting...
                </>
              ) : (
                'Delete Claim'
              )}
            </Button>
          </HStack>
      </ShowcaseDemoSection>

      {/* Full Page Overlay */}
      <ShowcaseDemoSection
        title="Full Page Overlay"
        description="Modal-style loading overlay for blocking operations"
       
      >
          <Card className="relative overflow-hidden">
            <CardContent>
              <VStack gap={4}>
                <Text weight="semibold">Policy Details</Text>
                <Text color="secondary">
                  Policy Number: POL-2024-001842
                </Text>
                <Text color="secondary">
                  Policyholder: John Smith
                </Text>
                <Text color="secondary">
                  Coverage: $500,000
                </Text>
              </VStack>
              {/* Overlay */}
              <div className="absolute inset-0 bg-white/80 flex flex-col items-center justify-center gap-3">
                <Spinner size="lg" />
                <Text weight="medium">Updating policy...</Text>
                <Text size="sm" color="secondary">Please do not close this window</Text>
              </div>
            </CardContent>
          </Card>
      </ShowcaseDemoSection>

      {/* Real-world Example */}
      <ShowcaseDemoSection
        title="Real-world Example"
        description="Inline loading states in an insurance dashboard"
       
      >
          <HStack gap={4} wrap="wrap">
            {/* Loading card */}
            <Card className="w-64">
              <CardContent>
                <VStack gap={4} align="center" className="py-6">
                  <Spinner size="lg" color="#0369A1" />
                  <VStack gap={1} align="center">
                    <Text weight="medium">Fetching Claims</Text>
                    <Text size="sm" color="secondary">This may take a moment</Text>
                  </VStack>
                </VStack>
              </CardContent>
            </Card>

            {/* Partial loading */}
            <Card className="w-64">
              <CardContent>
                <VStack gap={4}>
                  <HStack justify="between" align="center">
                    <Text weight="semibold">Recent Activity</Text>
                    <Spinner size="xs" />
                  </HStack>
                  <Text size="sm" color="secondary">
                    Checking for new claims and updates...
                  </Text>
                </VStack>
              </CardContent>
            </Card>

            {/* Processing state */}
            <Card className="w-64">
              <CardContent>
                <VStack gap={4}>
                  <HStack gap={3} align="center">
                    <Spinner size="sm" color="#047857" />
                    <VStack gap={0}>
                      <Text weight="medium">Processing</Text>
                      <Text size="xs" color="secondary">Claim CLM-2024-005678</Text>
                    </VStack>
                  </HStack>
                  <Text size="sm" color="secondary">
                    Verifying documentation and coverage details
                  </Text>
                </VStack>
              </CardContent>
            </Card>
          </HStack>
      </ShowcaseDemoSection>
    </VStack>
  );
}

export const SpinnerShowcase: ShowcaseDefinition = {
  id: 'spinner',
  name: 'Spinner',
  category: 'feedback',
  description: 'Loading indicator for asynchronous operations',
  propsDefinition: [
    {
      name: 'size',
      type: 'select',
      options: ['xs', 'sm', 'md', 'lg'],
      defaultValue: 'md',
      description: 'Size of the spinner (xs=16px, sm=24px, md=32px, lg=48px)',
    },
    {
      name: 'color',
      type: 'string',
      defaultValue: 'var(--color-primary-600, #486581)',
      description: 'Color of the spinner (any valid CSS color)',
    },
  ],
  accessibility: {
    ariaAttributes: [
      'aria-label="Loading"',
      'role="status" (via output element)',
    ],
    keyboardNav: [],
    notes: [
      'Includes visually hidden "Loading..." text for screen readers',
      'Uses output element for semantic status announcements',
      'Respects prefers-reduced-motion preference',
      'Stroke width scales proportionally with size',
    ],
  },
  component: SpinnerShowcaseComponent,
};
